import type { ReactNode } from 'react';
import type { ShipmentReportSplit } from '../../utils/client-reports/types';
import {
  ARRIVED_CLASS,
  IN_TRANSIT_CLASS,
  NOT_DEPARTED_CLASS,
  useReportLayout,
} from './reportLayout';
import { useReportFormat } from './useReportFormat';

// Piezas del documento que ve el cliente (link y PDF). Tiene estilo propio de
// informe impreso: líneas finas en vez de cards, tablas planas y cifras con
// ancho fijo. No es una pantalla de la app, por eso no usa Card ni Table de
// NextUI.

export const LABEL_CLASS =
  'text-[11px] font-medium uppercase tracking-[0.12em] text-default-500';

export const SectionHeader = ({
  number,
  title,
  aside,
}: {
  number: number;
  title: string;
  aside?: string;
}) => {
  const layout = useReportLayout();
  return (
    <div
      className={`flex flex-wrap items-baseline justify-between gap-x-6 gap-y-1 border-t-2 border-foreground pt-3 ${layout.headerSlack}`}
    >
      <div className='flex items-baseline gap-3'>
        <span className='text-sm font-semibold text-default-400'>
          {String(number).padStart(2, '0')}
        </span>
        <h3 className='text-xl font-semibold tracking-tight'>{title}</h3>
      </div>
      {aside ? <p className='text-sm text-default-500'>{aside}</p> : null}
    </div>
  );
};

// Franja de indicadores separados por líneas finas. Las celdas se acomodan
// solas en 2 o 4 columnas sin dejar bordes dobles.
export const MetricStrip = ({
  className,
  children,
}: {
  className: string;
  children: ReactNode;
}) => (
  <div
    className={`grid gap-px overflow-hidden rounded-medium border border-default-200 bg-default-200 ${className}`}
  >
    {children}
  </div>
);

export const Metric = ({
  label,
  value,
  suffix,
  detail,
  tone = 'default',
  children,
}: {
  label: string;
  value: string;
  // Texto chico al lado del valor, por ejemplo "a la fecha".
  suffix?: string;
  detail?: string;
  tone?: 'default' | 'danger' | 'muted';
  children?: ReactNode;
}) => (
  <div className='flex flex-col gap-1.5 bg-background p-4'>
    <span className={LABEL_CLASS}>{label}</span>
    <span className='flex items-baseline gap-1.5'>
      <span
        className={`text-2xl font-semibold tracking-tight ${
          tone === 'danger' ? 'text-danger-600' : tone === 'muted' ? 'text-default-400' : ''
        }`}
      >
        {value}
      </span>
      {suffix ? <span className='text-xs text-default-500'>{suffix}</span> : null}
    </span>
    {detail ? <span className='text-xs leading-relaxed text-default-500'>{detail}</span> : null}
    {children}
  </div>
);

// Avance de una meta dentro de un indicador: una frase con la cifra en
// negrita y una barra fina debajo.
export const ProgressLine = ({
  value,
  label,
  share,
}: {
  value: string;
  label: string;
  share: number;
}) => (
  <div className='mt-2 flex flex-col gap-2 border-t border-default-100 pt-3 text-xs'>
    <p className='text-default-500'>
      <span className='font-semibold text-foreground'>{value}</span> {label}
    </p>
    <div className='h-2 w-full overflow-hidden rounded-full bg-default-100'>
      <div
        className='h-full rounded-full bg-default-800'
        style={{ width: `${Math.min(Math.max(share, 0), 1) * 100}%` }}
      />
    </div>
  </div>
);

// Bloque con título en versalitas y un dato o leyenda a la derecha.
export const Panel = ({
  title,
  aside,
  children,
}: {
  title: string;
  aside?: ReactNode;
  children: ReactNode;
}) => {
  const layout = useReportLayout();
  return (
    <div className='flex flex-col gap-4'>
      <div
        className={`flex flex-wrap items-center justify-between gap-x-6 gap-y-2 border-b border-default-200 pb-2 ${layout.headerSlack}`}
      >
        <h4
          className={`text-xs font-semibold uppercase tracking-[0.12em] text-default-700 ${layout.panelTitle}`}
        >
          {title}
        </h4>
        {aside ? <div className='shrink-0 text-xs text-default-500'>{aside}</div> : null}
      </div>
      {children}
    </div>
  );
};

// Arribado / en tránsito dentro de un indicador: cada cifra junto a su
// etiqueta sobre una barra fina. Si no cabe en una línea, baja a la siguiente.
export const SplitLine = ({
  split,
  by,
  arrived,
  inTransit,
}: {
  split: ShipmentReportSplit;
  by: 'shipments' | 'ceq';
  arrived: string;
  inTransit: string;
}) => {
  const { t } = useReportFormat();
  return (
    <div className='mt-2 flex flex-col gap-2 border-t border-default-100 pt-3 text-xs'>
      <div className='flex flex-wrap items-baseline justify-between gap-x-3 gap-y-1 text-default-500'>
        <span>
          <span className='font-semibold text-foreground'>{arrived}</span> {t('arribado')}
        </span>
        <span>
          <span className='font-semibold text-foreground'>{inTransit}</span> {t('en tránsito')}
        </span>
      </div>
      <SplitBar split={split} max={split.arrived[by] + split.inTransit[by]} by={by} />
    </div>
  );
};

// Barra arribado / en tránsito, con el ancho relativo al máximo de la lista.
export const SplitBar = ({
  split,
  max,
  by,
}: {
  split: ShipmentReportSplit;
  max: number;
  by: 'shipments' | 'ceq';
}) => (
  <div className='flex h-2 w-full overflow-hidden rounded-full bg-default-100'>
    {(['arrived', 'inTransit'] as const).map((key) =>
      split[key][by] > 0 ? (
        <div
          key={key}
          className={key === 'arrived' ? ARRIVED_CLASS : IN_TRANSIT_CLASS}
          style={{ width: `${max ? (split[key][by] / max) * 100 : 0}%` }}
        />
      ) : null
    )}
  </div>
);

export const StatusLegend = ({
  withPlan = false,
  withNotDeparted = false,
}: {
  withPlan?: boolean;
  withNotDeparted?: boolean;
}) => {
  const { t } = useReportFormat();
  return (
    <div className='flex gap-4'>
      <span className='flex items-center gap-1.5 whitespace-nowrap'>
        <span className={`h-2.5 w-2.5 rounded-sm ${ARRIVED_CLASS}`} />
        {t('Arribado')}
      </span>
      <span className='flex items-center gap-1.5 whitespace-nowrap'>
        <span className={`h-2.5 w-2.5 rounded-sm ${IN_TRANSIT_CLASS}`} />
        {t('En tránsito')}
      </span>
      {withNotDeparted ? (
        <span className='flex items-center gap-1.5 whitespace-nowrap'>
          <span className={`h-2.5 w-2.5 rounded-sm ${NOT_DEPARTED_CLASS}`} />
          {t('Por zarpar')}
        </span>
      ) : null}
      {withPlan ? (
        <span className='flex items-center gap-1.5 whitespace-nowrap'>
          <span className='h-2.5 w-2.5 rounded-sm border border-dashed border-default-500' />
          {t('Plan comercial')}
        </span>
      ) : null}
    </div>
  );
};

// Estado en texto con un punto de color, para usar dentro de tablas.
export const StatusText = ({
  tone,
  children,
}: {
  tone: 'arrived' | 'inTransit' | 'danger' | 'warning' | 'strong' | 'muted';
  children: ReactNode;
}) => {
  const dot = {
    arrived: ARRIVED_CLASS,
    inTransit: IN_TRANSIT_CLASS,
    danger: 'bg-danger-500',
    warning: 'bg-warning-500',
    strong: 'bg-foreground',
    muted: 'bg-default-300',
  }[tone];
  const text = {
    arrived: '',
    inTransit: '',
    danger: 'font-medium text-danger-600',
    warning: 'text-warning-700',
    strong: 'font-medium',
    muted: 'text-default-500',
  }[tone];
  return (
    <span className={`inline-flex items-center gap-1.5 whitespace-nowrap ${text}`}>
      <span className={`h-1.5 w-1.5 rounded-full ${dot}`} />
      {children}
    </span>
  );
};

// Estado de un embarque en tránsito según los días que faltan para su ETA. Lo
// que llega en 7 días o menos va destacado.
const SOON_DAYS = 7;

export const EtaStatus = ({ daysToEta }: { daysToEta: number | null }) => {
  const { t } = useReportFormat();
  if (daysToEta == null) return <StatusText tone='warning'>{t('Sin ETA')}</StatusText>;
  if (daysToEta < 0) {
    return (
      <StatusText tone='danger'>{t('ETA vencida · +{{days}} d', { days: -daysToEta })}</StatusText>
    );
  }
  if (daysToEta === 0) return <StatusText tone='strong'>{t('Arriba hoy')}</StatusText>;
  return (
    <StatusText tone={daysToEta <= SOON_DAYS ? 'strong' : 'muted'}>
      {t('Arriba en {{days}} d', { days: daysToEta })}
    </StatusText>
  );
};

export type ReportColumn<T> = {
  key: string;
  label: string;
  align?: 'right';
  nowrap?: boolean;
  // En celular no se muestra (las barras: el % ya dice lo mismo).
  mobileHidden?: boolean;
  render: (row: T) => ReactNode;
};

// Tabla plana: encabezado en versalitas, filas separadas por líneas finas y
// una fila de total opcional. En celular, mobile='stack' (listas de
// embarques, con mucho texto) hace de cada fila un bloque con la primera
// columna de título y el resto en dos columnas; mobile='table' (cifras
// cortas) deja la tabla sin las columnas mobileHidden.
export const ReportTable = <T,>({
  columns,
  rows,
  rowKey,
  total,
  label,
  mobile = 'stack',
}: {
  columns: ReportColumn<T>[];
  rows: T[];
  rowKey: (row: T) => string;
  total?: Partial<Record<string, ReactNode>>;
  label: string;
  mobile?: 'stack' | 'table';
}) => {
  const layout = useReportLayout();
  const stacked = mobile === 'stack' && layout.stackClass !== null;
  const hiddenClass = (column: ReportColumn<T>) =>
    mobile === 'table' && column.mobileHidden ? layout.mobileHiddenClass : '';
  const cellClass = (column: ReportColumn<T>) =>
    `py-2.5 ${layout.cellGap} last:pr-0 align-middle ${column.align === 'right' ? 'text-right' : ''} ${
      column.nowrap ? 'whitespace-nowrap' : ''
    } ${hiddenClass(column)}`;

  const table = (
    <div className={`overflow-x-auto ${stacked ? layout.tableClass : ''}`}>
      <table aria-label={label} className='w-full border-collapse text-sm'>
        <thead>
          <tr className='border-b border-default-300'>
            {columns.map((column) => (
              <th
                key={column.key}
                className={`pb-2 ${layout.cellGap} last:pr-0 text-left align-bottom font-normal ${LABEL_CLASS} ${
                  column.align === 'right' ? 'text-right' : ''
                } ${hiddenClass(column)}`}
              >
                {column.label}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => (
            <tr key={rowKey(row)} className='border-b border-default-100'>
              {columns.map((column) => (
                <td key={column.key} className={cellClass(column)}>
                  {column.render(row)}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
        {total ? (
          <tfoot>
            <tr className='border-t border-default-300 font-semibold'>
              {columns.map((column) => (
                <td key={column.key} className={cellClass(column)}>
                  {total[column.key] ?? ''}
                </td>
              ))}
            </tr>
          </tfoot>
        ) : null}
      </table>
    </div>
  );
  if (!stacked) return table;

  const [first, ...rest] = columns;
  const stackRow = (key: string, title: ReactNode, cell: (column: ReportColumn<T>) => ReactNode, isTotal = false) => (
    <li
      key={key}
      className={`flex flex-col gap-2 py-3 ${isTotal ? 'border-t border-default-300 font-semibold' : 'border-b border-default-100'}`}
    >
      <div className='text-sm'>{title}</div>
      <dl className='grid grid-cols-2 gap-x-4 gap-y-2'>
        {rest.map((column) => {
          const value = cell(column);
          if (value == null || value === '') return null;
          return (
            <div
              key={column.key}
              className='flex min-w-0 flex-col gap-0.5'
            >
              <dt className={LABEL_CLASS}>{column.label}</dt>
              <dd className='text-sm'>{value}</dd>
            </div>
          );
        })}
      </dl>
    </li>
  );

  return (
    <>
      {table}
      <ul aria-label={label} className={layout.stackClass ?? ''}>
        {rows.map((row) => stackRow(rowKey(row), first.render(row), (column) => column.render(row)))}
        {total
          ? stackRow('__total__', total[first.key] ?? '', (column) => total[column.key], true)
          : null}
      </ul>
    </>
  );
};

export const EmptySection = ({ children }: { children: ReactNode }) => (
  <p className='rounded-medium border border-dashed border-default-200 p-6 text-sm text-default-500'>
    {children}
  </p>
);
